import { useEffect, useState } from "react";
import { PostHogProvider } from "posthog-js/react";
import type { PostHogConfig } from "posthog-js";

type AnalyticsProviderProps = {
	children: React.ReactNode;
};

const DEFAULT_API_HOST = "https://us.i.posthog.com"; // default host
const ENV = import.meta.env.VITE_ENV;

/**
 * Utility to check if a URL is reachable.
 * HEAD request and timeout wrapper to avoid long hangs.
 */
const checkHostReachable = async (
	url: string,
	timeout = 2000,
): Promise<boolean> => {
	const controller = new AbortController();
	const id = setTimeout(() => controller.abort(), timeout);

	try {
		await fetch(url, {
			method: "HEAD",
			mode: "no-cors", // still may not throw on non-200s in no-cors
			signal: controller.signal,
		});
		clearTimeout(id);
		// no-cors won't give us status codes, so if it doesn't throw, assume reachable
		return Promise.resolve(true);
	} catch (err) {
		clearTimeout(id);

		return Promise.resolve(false);
	}
};

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
	children,
}) => {
	const [apiHost, setApiHost] = useState<string | null>(null);

	useEffect(() => {
		const checkConnectivity = async () => {
			const reachable = await checkHostReachable(DEFAULT_API_HOST);
			const proxyHost = import.meta.env.VITE_BEACON_URL; // Update to real proxy

			/**
			 * If we can reach PostHog directly, use that host to save us some money. Otherwise,
			 * use the proxy host.
			 */
			setApiHost(reachable ? DEFAULT_API_HOST : proxyHost);
		};

		// Defer checking without blocking render
		checkConnectivity();
	}, []);

	const postHogApiKey = import.meta.env.VITE_PUBLIC_POST_HOG_KEY;

	if (!postHogApiKey) {
		console.warn("Missing PostHog API key in environment.");
		return <>{children}</>; // Render children without analytics in fallback
	}

	if (!apiHost) {
		// Still checking reachability - don't block app
		return <>{children}</>;
	}

	const postHogOptions: Partial<PostHogConfig> = {
		api_host: apiHost,
		person_profiles: "always", // turn this off if we're running a feature flag that doesn't need to persist across authentication
		disable_session_recording: ENV === "development",
	};

	return (
		<PostHogProvider apiKey={postHogApiKey} options={postHogOptions}>
			{children}
		</PostHogProvider>
	);
};
